import React, { useState, useEffect, useRef } from 'react';
import { UserProfile, Conversation, Message } from '../../../app/domain/types/index.ts';
import { Search, MoreVertical, Phone, Video, Paperclip, Send, Check, CheckCheck, Circle, Clock, ShieldCheck, Users, Filter, ChevronLeft } from 'lucide-react';

interface MessagingSystemProps {
  user: UserProfile;
}

const MessagingSystem: React.FC<MessagingSystemProps> = ({ user }) => {
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [inputText, setInputText] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'professional' | 'group'>('all');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Mock Conversations
  const conversations: Conversation[] = [
    {
      id: '1',
      name: 'Dr. James Smith',
      role: 'Endocrinologist',
      avatar: 'JS',
      type: 'professional',
      lastMessage: 'Your latest blood work looks promising. Let\'s keep the current dosage.',
      lastMessageTime: '10:30 AM',
      unreadCount: 1,
      isOnline: true,
      isVerified: true,
      availabilityStatus: 'available'
    },
    {
      id: '2',
      name: 'Sarah M.',
      role: 'Community Member',
      avatar: 'SM',
      type: 'direct',
      lastMessage: 'Thanks for the tip about the cold rice! Huge difference.',
      lastMessageTime: 'Yesterday',
      unreadCount: 0,
      isOnline: false
    },
    {
      id: '3',
      name: 'Type 2 Support Group',
      role: 'Group Chat',
      avatar: 'T2',
      type: 'group',
      lastMessage: 'Maria: Has anyone tried the new meal plan for next week?',
      lastMessageTime: 'Yesterday',
      unreadCount: 2,
      isOnline: true
    },
    {
      id: '4',
      name: 'Dr. Maria Garcia',
      role: 'Dietitian',
      avatar: 'MG',
      type: 'professional',
      lastMessage: 'I sent over the updated meal plan. Let me know if you have questions.',
      lastMessageTime: 'Mon',
      unreadCount: 0,
      isOnline: false,
      isVerified: true,
      availabilityStatus: 'away'
    }
  ];

  // Mock Messages Store
  const [messagesData, setMessagesData] = useState<Record<string, Message[]>>({
    '1': [
      { id: 'm1', senderId: 'other', text: 'Good morning! I reviewed your logs from last week.', timestamp: '10:28 AM', read: true, type: 'text' },
      { id: 'm2', senderId: 'other', text: 'Your latest blood work looks promising. Let\'s keep the current dosage.', timestamp: '10:30 AM', read: false, type: 'text' }
    ],
    '2': [
      { id: 'm1', senderId: 'me', text: 'Hey Sarah, did you try the resistant starch method?', timestamp: 'Yesterday', read: true, type: 'text' },
      { id: 'm2', senderId: 'other', text: 'Thanks for the tip about the cold rice! Huge difference.', timestamp: 'Yesterday', read: true, type: 'text' }
    ],
    '3': [
      { id: 'm1', senderId: 'other', text: 'Welcome to the group everyone!', timestamp: 'Mon', read: true, type: 'text' },
      { id: 'm2', senderId: 'me', text: 'Happy to be here.', timestamp: 'Mon', read: true, type: 'text' },
      { id: 'm3', senderId: 'other', text: 'Has anyone tried the new meal plan for next week?', timestamp: 'Yesterday', read: false, type: 'text' }
    ],
    '4': [
        { id: 'm1', senderId: 'other', text: 'I sent over the updated meal plan. Let me know if you have questions.', timestamp: 'Mon', read: true, type: 'text' }
    ]
  });

  const filteredConversations = conversations.filter(c => {
    if (activeFilter === 'all') return true;
    return c.type === activeFilter;
  });

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messagesData, selectedConversation]);

  const handleSendMessage = () => {
    if (!inputText.trim() || !selectedConversation) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      senderId: 'me',
      text: inputText,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      read: false,
      type: 'text'
    };

    setMessagesData(prev => ({
      ...prev,
      [selectedConversation.id]: [...(prev[selectedConversation.id] || []), newMessage]
    }));
    setInputText('');
  };

  return (
    <div className="h-full flex flex-col md:flex-row overflow-hidden bg-[#FAFAFA] dark:bg-[#0f0f0f]">
      
      {/* LEFT PANE: Conversation List */}
      <div className={`w-full md:w-[380px] flex flex-col border-r border-stone-200 dark:border-stone-800 bg-white/50 dark:bg-stone-900/50 md:flex ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
        
        {/* Header */}
        <div className="p-4 border-b border-stone-100 dark:border-stone-800 bg-white/80 dark:bg-stone-900/80 backdrop-blur-sm">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-xl font-light text-gray-800 dark:text-white">Messages</h1>
            <button className="p-2 bg-health-50 dark:bg-health-900/20 text-health-600 dark:text-health-400 rounded-full hover:bg-health-100 dark:hover:bg-health-900/40 transition-colors">
              <MoreVertical size={18} />
            </button>
          </div>
          
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input 
              type="text" 
              placeholder="Search messages..." 
              className="w-full pl-10 pr-4 py-2.5 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl text-sm focus:outline-none focus:border-health-500 dark:focus:border-health-400 transition-all text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
            />
          </div>

          {/* Filters */}
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            <button 
              onClick={() => setActiveFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${activeFilter === 'all' ? 'bg-gray-800 text-white dark:bg-white dark:text-gray-900' : 'bg-stone-100 dark:bg-stone-800 text-gray-600 dark:text-gray-400 hover:bg-stone-200 dark:hover:bg-stone-700'}`}
            >
              All Messages
            </button>
            <button 
              onClick={() => setActiveFilter('professional')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${activeFilter === 'professional' ? 'bg-health-600 text-white' : 'bg-stone-100 dark:bg-stone-800 text-gray-600 dark:text-gray-400 hover:bg-stone-200 dark:hover:bg-stone-700'}`}
            >
              Professional
            </button>
            <button 
              onClick={() => setActiveFilter('group')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${activeFilter === 'group' ? 'bg-trust-600 text-white' : 'bg-stone-100 dark:bg-stone-800 text-gray-600 dark:text-gray-400 hover:bg-stone-200 dark:hover:bg-stone-700'}`}
            >
              Groups
            </button>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto no-scrollbar p-2">
          {filteredConversations.map(convo => (
            <div 
              key={convo.id}
              onClick={() => setSelectedConversation(convo)}
              className={`p-3 rounded-2xl cursor-pointer transition-all mb-1 border border-transparent ${selectedConversation?.id === convo.id ? 'bg-white dark:bg-stone-800 shadow-sm border-stone-200 dark:border-stone-700' : 'hover:bg-stone-50 dark:hover:bg-stone-800/50'}`}
            >
              <div className="flex gap-3">
                <div className="relative">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold shadow-sm ${convo.type === 'professional' ? 'bg-health-100 dark:bg-health-900/30 text-health-700 dark:text-health-400' : convo.type === 'group' ? 'bg-trust-100 dark:bg-trust-900/30 text-trust-700 dark:text-trust-400' : 'bg-gray-200 dark:bg-stone-700 text-gray-600 dark:text-gray-300'}`}>
                    {convo.avatar}
                  </div>
                  {convo.isOnline && (
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-stone-800 rounded-full"></div>
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-start mb-0.5">
                    <div className="flex items-center gap-1">
                      <h3 className={`font-medium text-sm truncate ${convo.unreadCount > 0 ? 'text-gray-900 dark:text-white font-semibold' : 'text-gray-700 dark:text-gray-300'}`}>{convo.name}</h3>
                      {convo.isVerified && <ShieldCheck size={12} className="text-health-500" />}
                    </div>
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">{convo.lastMessageTime}</span>
                  </div>
                  
                  {convo.role && <p className="text-[10px] text-trust-600 dark:text-trust-400 font-medium mb-1">{convo.role}</p>}
                  
                  <div className="flex justify-between items-center">
                    <p className={`text-xs truncate max-w-[180px] ${convo.unreadCount > 0 ? 'text-gray-800 dark:text-gray-200 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                      {convo.type === 'group' && convo.unreadCount > 0 && <span className="text-gray-900 dark:text-white">Maria: </span>}
                      {convo.lastMessage}
                    </p>
                    {convo.unreadCount > 0 && (
                      <span className="min-w-[18px] h-[18px] px-1 bg-health-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {convo.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT PANE: Message Thread */}
      <div className={`flex-1 flex flex-col h-full bg-[#FAFAFA] dark:bg-[#0f0f0f] md:flex ${!selectedConversation ? 'hidden md:flex' : 'flex'}`}>
        {selectedConversation ? (
          <>
            {/* Thread Header */}
            <div className="px-6 py-4 border-b border-stone-200 dark:border-stone-800 bg-white/80 dark:bg-stone-900/80 backdrop-blur-md flex justify-between items-center shadow-sm z-10">
              <div className="flex items-center gap-3">
                <button className="md:hidden mr-1 text-gray-500 dark:text-gray-400" onClick={() => setSelectedConversation(null)}>
                  <ChevronLeft size={24} />
                </button>
                <div className="relative">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-sm ${selectedConversation.type === 'professional' ? 'bg-health-100 dark:bg-health-900/30 text-health-700 dark:text-health-400' : selectedConversation.type === 'group' ? 'bg-trust-100 dark:bg-trust-900/30 text-trust-700 dark:text-trust-400' : 'bg-gray-200 dark:bg-stone-700 text-gray-600 dark:text-gray-300'}`}>
                    {selectedConversation.avatar}
                  </div>
                  {selectedConversation.isOnline && (
                    <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white dark:border-stone-900 rounded-full"></div>
                  )}
                </div>
                <div>
                  <h2 className="font-semibold text-gray-800 dark:text-white flex items-center gap-1">
                    {selectedConversation.name}
                    {selectedConversation.isVerified && <ShieldCheck size={14} className="text-health-500" />}
                  </h2>
                  <div className="flex items-center gap-2">
                    {selectedConversation.role && <span className="text-xs text-trust-600 dark:text-trust-400 font-medium bg-trust-50 dark:bg-trust-900/20 px-1.5 py-0.5 rounded">{selectedConversation.role}</span>}
                    {selectedConversation.availabilityStatus === 'available' && <span className="text-[10px] text-green-600 dark:text-green-400 flex items-center gap-1"><Circle size={6} fill="currentColor" /> Available now</span>}
                    {selectedConversation.availabilityStatus === 'away' && <span className="text-[10px] text-orange-500 flex items-center gap-1"><Clock size={10} /> Away - Replies in ~2h</span>}
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-full transition-colors">
                  <Phone size={20} />
                </button>
                <button className="p-2 text-gray-400 hover:text-health-600 dark:hover:text-health-400 hover:bg-health-50 dark:hover:bg-health-900/20 rounded-full transition-colors">
                  <Video size={20} />
                </button>
                <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-full transition-colors">
                  <MoreVertical size={20} />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-[#FAFAFA] dark:bg-[#0f0f0f]" ref={scrollRef}>
              {/* Timestamp Separator */}
              <div className="flex justify-center">
                <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wider bg-stone-100 dark:bg-stone-800 px-3 py-1 rounded-full">Today</span>
              </div>

              {messagesData[selectedConversation.id]?.map((msg) => (
                <div key={msg.id} className={`flex ${msg.senderId === 'me' ? 'justify-end' : 'justify-start'}`}>
                  {msg.senderId !== 'me' && selectedConversation.type === 'group' && (
                     <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-stone-700 mr-2 flex-shrink-0 flex items-center justify-center text-[10px] font-bold text-gray-600 dark:text-gray-300">M</div>
                  )}
                  <div className={`max-w-[70%] group`}>
                     {msg.senderId !== 'me' && selectedConversation.type === 'group' && (
                       <p className="text-[10px] text-gray-500 dark:text-gray-400 ml-1 mb-1">Maria</p>
                     )}
                    <div className={`p-4 rounded-2xl text-sm shadow-sm relative ${
                      msg.senderId === 'me' 
                      ? 'bg-gray-900 dark:bg-stone-700 text-white rounded-tr-none' 
                      : 'bg-white dark:bg-stone-800 text-gray-700 dark:text-gray-200 border border-stone-100 dark:border-stone-700 rounded-tl-none'
                    }`}>
                      {msg.text}
                      
                      {/* Message Meta */}
                      <div className={`flex items-center justify-end gap-1 mt-1 ${msg.senderId === 'me' ? 'text-gray-400' : 'text-gray-300 dark:text-gray-500'}`}>
                        <span className="text-[10px]">{msg.timestamp}</span>
                        {msg.senderId === 'me' && (
                          msg.read ? <CheckCheck size={12} className="text-health-400" /> : <Check size={12} />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Input Area */}
            <div className="p-4 bg-white dark:bg-stone-900 border-t border-stone-200 dark:border-stone-800">
              {selectedConversation.availabilityStatus === 'away' && (
                  <div className="mb-3 px-4 py-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800 rounded-lg text-xs text-orange-700 dark:text-orange-400 flex items-center gap-2">
                    <Clock size={14} />
                    Note: {selectedConversation.name} is currently away. Expect a response by tomorrow morning.
                  </div>
              )}
              <div className="flex items-end gap-2 bg-stone-50 dark:bg-stone-800 p-2 rounded-3xl border border-stone-200 dark:border-stone-700 focus-within:border-health-400 dark:focus-within:border-health-600 focus-within:ring-2 focus-within:ring-health-100 dark:focus-within:ring-health-900/20 transition-all">
                <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-full transition-colors">
                  <Paperclip size={20} />
                </button>
                <input 
                  type="text" 
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder={`Message ${selectedConversation.name}...`}
                  className="flex-1 bg-transparent border-none focus:ring-0 py-2.5 px-2 text-sm max-h-32 overflow-y-auto text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                />
                <button 
                  onClick={handleSendMessage}
                  disabled={!inputText.trim()}
                  className={`p-2.5 rounded-full transition-all ${inputText.trim() ? 'bg-health-600 text-white shadow-md hover:scale-105 active:scale-95' : 'bg-stone-200 dark:bg-stone-700 text-gray-400 cursor-not-allowed'}`}
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 p-8">
            <div className="w-24 h-24 bg-stone-100 dark:bg-stone-800 rounded-full flex items-center justify-center mb-6">
              <Users size={40} className="text-gray-300 dark:text-gray-600" />
            </div>
            <h2 className="text-xl font-medium text-gray-700 dark:text-gray-200 mb-2">Your Conversations</h2>
            <p className="text-center max-w-sm text-sm">
              Select a chat to communicate with your healthcare team, support groups, or friends.
            </p>
            <button className="mt-8 px-6 py-2.5 bg-health-600 text-white rounded-full text-sm font-medium hover:bg-health-700 transition-all shadow-lg shadow-health-600/20">
              Start New Message
            </button>
          </div>
        )}
      </div>

    </div>
  );
};

export default MessagingSystem;